import { Component, ComponentFactoryResolver, Injector, ViewChild } from '@angular/core';
import { ContextMenuElement, ShareableEntity } from '@m4u-angular/base/classes/BaseClasses';
import { BasePageCRUDComponent } from '@m4u-angular/base/component/BasePageCRUDComponent';
import { ServiceBagService, ServiceBagServices } from '@m4u-angular/base/component/ServiceBag';
import { AttachedFile, BaseEntitiesResponse, BaseState, FileView, NotificationServer, NotificationTemplate } from '@m4u-angular/base/entities/BaseEntities';
import { NotificationServerEndpoint } from '@m4u-angular/core/endpoints/notification-server.endpoint';
import { NotificationTemplateEndpoint } from '@m4u-angular/core/endpoints/notification-template.endpoint';
import { EnumMessageBoxTypes, EnumNotificationTemplateEditingMode, EnumPageDisplayModes, EnumPrintingType } from '@m4u-angular/core/framework/sysenums.enum';
import { HtmlBoxDialogComponent } from '@m4u-angular/ui/dialogs/htmlbox-dialog/htmlbox-dialog.component';
import { MessageBoxDialogComponent } from '@m4u-angular/ui/dialogs/messagebox-dialog/messageboxdialog.component';
import { DxTextAreaComponent, DxTextBoxComponent } from 'devextreme-angular';
import { NotificationPreviewDialogComponent } from '../notification-preview-dialog/notification-preview-dialog.component';
import { Utils } from '@m4u-angular/core/helpers/Utils';
import { FileEndpoint } from '@m4u-angular/core/endpoints/file.endpoint';

export class State extends BaseState {
    NotificationServers: NotificationServer[] = [];

    IsUserCustomTemplateOriginal: boolean;
    Pages = [];
    Entities = [];
    ShareableEntities: ShareableEntity[] = [];
    TypeDictionary = {}; // compiled type - key, actual type - value
    LabelDictionary = {}; // compiled type - key, label - value
}

@Component({
    providers: ServiceBagServices,
    selector: 'app-notification-template',
    templateUrl: './notification-template.component.html',
    styleUrls: ['./notification-template.component.scss']
})
export class NotificationTemplateComponent extends BasePageCRUDComponent<NotificationTemplate, State, NotificationTemplateEndpoint> {

    @ViewChild('htmlEditorDialog') htmlEditorDialog: HtmlBoxDialogComponent;
    @ViewChild('previewDialog') previewDialog: NotificationPreviewDialogComponent;
    @ViewChild('messageBox') messageBox: MessageBoxDialogComponent;
    @ViewChild('htmlBody') htmlBody: DxTextAreaComponent;
    @ViewChild('phone') phone: DxTextBoxComponent;

    get SelectedNotificationServer() {
        return this.State.NotificationServers && this.Entity.NotificationServerID && this.Entity.NotificationServerID === -999 
            ? this.State.NotificationServers.find(n => n.EntryID !== -999) 
            : this.State.NotificationServers.find(n => n.EntryID === this.Entity.NotificationServerID);
    }

    // eslint-disable-next-line max-len
    multipleEmailPattern: any = /^(([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)(\s*;\s*|\s*$))*$/;
    infoUPhonePattern: any = /^[a-zA-Z0-9\s]*$/;

    constructor(
        A: ServiceBagService,
        private endpoint: NotificationTemplateEndpoint,
        private notificationServerEndpoint: NotificationServerEndpoint,
        private injector: Injector,
        private componentFactoryResolver: ComponentFactoryResolver,
        private fileEndpoint: FileEndpoint) {
        super(A, 'NotificationTemplateComponent', NotificationTemplate, State, endpoint, EnumPageDisplayModes.Window);
    }

    protected override OnInitComponent(): void {
        this.LoadNotificationServers();
    }

    protected override OnInitComponentFinished(): void {
        this.SetShareableEntities();
        this.SetPages();
    }

    protected override OnEntityInitObjectName() {
        return this.Entity && this.Entity.Name;
    }

    protected override OnEntitySaved() {
        this.NavigateBack();
    }

    protected override OnIsAllowDuplicate(): boolean {
        return true;
    }

    protected override OnEntityDuplicated() {
        this.Entity.Name = "";
    }

    protected override OnEntityNew() {
        !this.Entity.EntityBindingPageType && (this.Entity.EntityBindingPageType = EnumPrintingType.None);
        this.Entity.IsUserCustomTemplate = true;
    }

    protected override OnInitContextMenus(): ContextMenuElement[] {
        if (this.IsAddMode) return undefined;

        return [{
            text: this.RC('titleDelete'),
            visible: true,
            onClick: this.OnDelete.bind(this)
        }];
    }

    protected override OnEntityLoaded(): void {
        this.State.IsUserCustomTemplateOriginal = this.Entity.IsUserCustomTemplate;
        !this.Entity.CrystalTemplateFileView && (this.Entity.CrystalTemplateFileView = new FileView);
        !this.Entity.NotificationServerID && (this.Entity.NotificationServerID = -999);
    }

    protected override OnEntityValidate(): boolean {

        // prevent sabing empty template
        if (!this.IsBodyFilled() && !this.Entity.IsSharePdf) {
            this.UI.StopMessage(this.RC("msgNotificationTemplateEmptyError"));
            return false;
        }

        if (this.Entity.EditingMode == EnumNotificationTemplateEditingMode.EditPrefix) {
            const prefixParam = "$PrefixBody$";

            if (this.Entity.HTMLBody && this.Entity.HTMLBody.indexOf(prefixParam) == -1 ||
                this.Entity.SMSBody && this.Entity.SMSBody.indexOf(prefixParam) == -1 ||
                this.Entity.PushMessageBody && this.Entity.PushMessageBody.indexOf(prefixParam) == -1) {
                this.UI.StopMessage(this.RC("msgNotificationTemplatePrefixNotFoundError"));
                return false;
            }
        }

        if (this.Entity.IsSharePdf && (!this.Entity.CrystalTemplateFileID && !this.Entity.CrystalTemplateFileView?.FileData?.length)) {
            this.UI.StopMessage(this.RC("msgNotificationTemplateFileMissingError"));
            return false;
        }

        return true;
    }

    private LoadNotificationServers() {
        this.Do(this.notificationServerEndpoint.GetList(), false, (response: BaseEntitiesResponse<NotificationServer>) => {
            if (response.Entities.length === 1) {
                const defaultNotificationServer = {
                    EntryID: -999, 
                    SMTPServer: this.RC('lblUseDefault')
                } as any;
                this.State.NotificationServers = [defaultNotificationServer, ...response.Entities];
                if (!this.Entity.NotificationServerID) this.Entity.NotificationServerID = -999;
            } else this.State.NotificationServers = response.Entities;
        });
    }

    private SetShareableEntities() {
        const shareableEntities = this.A.AppConfigService.AppConfig.ShareableEntities;

        let moduleName = '';

        shareableEntities.forEach(shareableEntity => {

            //creating Page component and getting ComponentName
            for (let i = 0; i < shareableEntity.PageComponents.length; i++) {
                const page = shareableEntity.PageComponents[i];
                const componentFactory = this.componentFactoryResolver.resolveComponentFactory(page);
                const componentRef = componentFactory.create(this.injector);
                if (!moduleName) moduleName = componentRef.instance.ModuleName;

                this.State.TypeDictionary[shareableEntity.PageComponents[i].toString()] = componentRef.instance.ComponentName;
                this.State.LabelDictionary[shareableEntity.PageComponents[i].toString()] = this.A.ResourcesService.RC(`Pages.${componentRef.instance.ComponentName}`, moduleName);
            }
        });

        shareableEntities.forEach(shareableEntity => {
            // creating object of entity and getting TypeName of entity
            if (shareableEntity.EntityType) {
                const obj = new shareableEntity.EntityType;
                this.State.TypeDictionary[shareableEntity.EntityType.toString()] = obj.TypeName;
                this.State.LabelDictionary[shareableEntity.EntityType.toString()] = this.A.ResourcesService.RC(obj.TypeName, moduleName);
            }
        });

        this.State.ShareableEntities = shareableEntities;
    }

    private SetPages() {
        this.State.Pages = this.State.ShareableEntities.filter(x => x.PageType == this.Entity.EntityBindingPageType && (!this.Entity.EntityType
            || this.State.TypeDictionary[x.EntityType?.toString()] == this.Entity.EntityType))
            .map(x => x.PageComponents).flat().map(x => ({ PageComponent: this.State.TypeDictionary[x.toString()], Name: this.State.LabelDictionary[x.toString()] }));

        if (this.Entity.EntityType && this.State.Pages.length == 1) {
            this.Entity.PageName = this.State.Pages[0].PageComponent;
        }

        let shareableEntities = this.State.ShareableEntities.filter(x => x.PageType == this.Entity.EntityBindingPageType && x.EntityType);
        shareableEntities = shareableEntities.filter((x, i, a) => a.findIndex(s => s.EntityType == x.EntityType) == i);

        this.State.Entities = shareableEntities.map(x => ({ Name: this.State.LabelDictionary[x.EntityType?.toString()], Entity: this.State.TypeDictionary[x.EntityType?.toString()] }));
    }

    IsShowEditingMode = () => this.IsBodyFilled();

    IsBodyFilled = () => !!this.Entity.HTMLBody || !!this.Entity.PushMessageBody || !!this.Entity.SMSBody || !!this.Entity.WhatsAppBody;

    OnShowInEditor() {
        this.htmlEditorDialog.Language = this.Entity.Language;
        this.htmlEditorDialog.HTMLBody = this.Entity.HTMLBody;
        this.htmlEditorDialog.ShowDialogShort((state: any) => {
            this.Entity.HTMLBody = state.HTMLBody;
        });
    }

    OnShowPreview() {
        this.previewDialog.HTMLBody = this.Entity.HTMLBody;
        this.previewDialog.ShowDialogShort(() => {});
    }

    OnEntityBindingPageTypeChanged(e) {
        if (e.event) {
            this.Entity.PageName = null;
            this.Entity.EntityType = null;
        }
        
        this.SetPages();
    }

    OnEntityTypeChanged(e) {
        if (e.event) {
            this.Entity.PageName = null;
        }

        this.SetPages();
    }

    OnPageNameChanged(e) {
        if (!e.event) return;
        this.SetPages();
    }

    OnDownloadCrystalTemplateFile() {
        this.Do(this.fileEndpoint.GetViewByID(this.Entity.CrystalTemplateFileView.EntryID, true), true, response => {
            const file = new AttachedFile;
            file.Extension = this.Entity.CrystalTemplateFileView.Extension;
            file.Name = this.Entity.CrystalTemplateFileView.Name;
            Utils.DoFileDownload(file, Utils.ConvertToBase64(response.Entity.FileData), this.Entity.CrystalTemplateFileView.ContentType);
        });
    }

    override OnDelete(): void {
        this.messageBox.Type = EnumMessageBoxTypes.YesNo;
        this.messageBox.Prompt = this.RC('msgDeleteNotificationTemplateQuestion');
        this.messageBox.ShowDialogShort(() => {
            this.DeleteEntity(); 
        });
    }

    OnEmailSectionToggle(isOpened: boolean) {
        if (isOpened) setTimeout(() => this.htmlBody && this.htmlBody.instance.repaint());
    }

    OnPhoneSectionToggle(isOpened: boolean) {
        if (isOpened) setTimeout(() => this.phone && this.phone.instance.repaint());
    }

    OnChangeIsUserCustomTemplate() {
        const template = Utils.CloneObject(this.Entity);
        template.IsUserCustomTemplate = this.Entity.IsUserCustomTemplate ? false : true;
        this.Do(this.Endpoint.UpdateIsUserCustomTemplate(template), true, (response) => {
            this.Entity.IsUserCustomTemplate = response.Data;
        });
    }

    OnFileSelected(e) {
        const files = e?.target?.files;
        if (!files || files?.length == 0) return;

        const file = files[0];
        const fileReader = new FileReader();

        fileReader.addEventListener("load", (event) => {
            const fileBase64 = event.target.result as string;
            const bytes = Utils.ConvertToBytesArray(fileBase64);

            if (!this.Entity.CrystalTemplateFileView) this.Entity.CrystalTemplateFileView = new FileView;
            this.Entity.CrystalTemplateFileView.FileData = bytes;
            this.Entity.CrystalTemplateFileView.Name = this.Entity.CrystalTemplateFileView.FullName = file.name;
            this.Entity.CrystalTemplateFileView.Extension = null;
        });

        fileReader.readAsDataURL(files[0]);
    }
}
